import { useState } from 'react';
import { Badge, Card, CardContent, CardHeader } from '@forge/common';
import type { DashboardStatus, Milestone } from '@/api/dashboard';
import ProgressRing from './ProgressRing';
import ShowMoreToggle from './ShowMoreToggle';
import { statusStyles } from './statusStyles';

interface MilestoneListProps {
  milestones: Milestone[];
  limit?: number;
}

const statusOrder: Record<DashboardStatus, number> = {
  in_progress: 0,
  blocked: 1,
  todo: 2,
  done: 3,
};

const MilestoneList = ({ milestones, limit = 5 }: MilestoneListProps) => {
  const [expanded, setExpanded] = useState(false);

  const sortedMilestones = [...milestones].sort((left, right) => {
    return (
      statusOrder[left.status] - statusOrder[right.status] ||
      right.completionPct - left.completionPct ||
      left.sortOrder - right.sortOrder
    );
  });

  const visibleMilestones = expanded
    ? sortedMilestones
    : sortedMilestones.slice(0, limit);
  const hiddenCount = sortedMilestones.length - sortedMilestones.slice(0, limit).length;

  return (
    <Card className="h-full border border-gray-200/80 shadow-sm dark:border-gray-800">
      <CardHeader
        title="Key milestones"
        subtitle="Top execution priorities and delivery readiness"
      />
      <CardContent top="none">
        <ul className="divide-y divide-gray-200/70 dark:divide-gray-800">
          {visibleMilestones.map((milestone) => {
            const style = statusStyles[milestone.status];

            return (
              <li
                key={milestone.id}
                className="flex items-center gap-4 py-3 first:pt-0 last:pb-0"
              >
                <ProgressRing
                  value={milestone.completionPct}
                  colorClass={style.ringClass}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {milestone.title}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-color-tertiary">
                    <span className="shrink-0 font-medium">
                      {milestone.category}
                    </span>
                    {milestone.description ? (
                      <>
                        <span aria-hidden>•</span>
                        <span className="truncate">
                          {milestone.description}
                        </span>
                      </>
                    ) : undefined}
                  </div>
                </div>
                <Badge color={style.badgeColor} type={style.badgeType}>
                  {style.label}
                </Badge>
              </li>
            );
          })}
        </ul>
        {hiddenCount > 0 ? (
          <ShowMoreToggle
            expanded={expanded}
            onToggle={() => setExpanded((current) => !current)}
            moreLabel={`Show all ${sortedMilestones.length} milestones`}
          />
        ) : undefined}
      </CardContent>
    </Card>
  );
};

export type { MilestoneListProps };
export default MilestoneList;
